import {
  createInjectionState, useMemoize
} from '@vueuse/core';
import {
  computed, Ref, ref, watch, watchEffect
} from 'vue';
import {
  ProjectOffer, ProjectOfferSearchRequest, ProjectOfferSearchResponse, ProjectWithOffers
} from '@/models/MyProjects';
import { getProjectOffer, searchOffers } from '@/services/OffersService';
import useUser from '@/mixins/useUser';
import ProjectForumService from '@/services/ProjectForumService';
import { logDebug } from '@/utils/logger';
import { Project } from '@/views/project-forum/models/ProjectForum';
import { AxiosError } from 'axios';
import usePageState from '@/state/pageState';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import { useRoute } from 'vue2-helpers/vue-router';
import RecommendationService from '@/services/RecommendationService';
import { ExternalUserProject, RecommendationInitiatedBy } from '@/models/Recommendations';
import { useStore } from 'vue2-helpers/vuex';

interface ReceivedOffersOptions {
  isNarrow: Ref<boolean>;
}

const [useMyProjectsProvider, useOffersInjected] = createInjectionState((options: ReceivedOffersOptions) => {
  const receivedProjectsWithOffers = ref<ProjectWithOffers[]>([]);
  const visibleReceivedProjectsWithOffers = ref<ProjectWithOffers[]>([]);

  const madeProjectsWithOffers = ref<ProjectWithOffers[]>([]);
  const selectedProjectWithOffers = ref<ProjectWithOffers | null>(null);
  const isLoading = ref(false);
  const hasChanges = ref(false);
  const isStatusLayerOpen = ref(false);
  const statusLayerProject = ref<Project | null>(null);
  const projectIdsToKeepInProjectFilter = ref<string[]>([]);
  const { globalErrorMessage } = usePageState();
  const myProjects = ref<ExternalUserProject[]>([]);
  const store = useStore();

  const {
    companyId, userId, isCustomer, isFreelancer
  } = useUser('self');

  const loadMyProjects = async () => {
    const myProjectsResponse = await RecommendationService.getMyProjects();
    myProjects.value = myProjectsResponse.externalUserProjectList ?? [];
    if (store.state.projectForum.project.id?.length > 0) {
      store.dispatch('projectForum/fetchProject', store.state.projectForum.project.id);
    }
  };

  const projectsCreatedByMe = computed(() => myProjects.value.filter((project) => project.initiatedBy === RecommendationInitiatedBy.Creation));

  const route = useRoute();

  watch(isStatusLayerOpen, (is) => {
    if (is) return;
    statusLayerProject.value = null;
  });

  const loadOffer = useMemoize(
    async (projectId: string, offerId: string):Promise<ProjectOffer | null> => {
      isLoading.value = true;
      const offer = await getProjectOffer(projectId, offerId).catch((err:AxiosError) => {
        globalErrorMessage.value = {
          i18Key: 'my-projects.errors.load-offer-details-error',
          code: getAxiosResponseErrorCode(err),
          component: 'useReceivedOffers.ts'
        };
        return null;
      });
      isLoading.value = false;
      return offer;
    }
  );

  const loadProject = useMemoize(
    async (projectId: string):Promise<ProjectWithOffers['project'] | null> => {
      isLoading.value = true;
      const project = await ProjectForumService.getProject(projectId).catch((err:AxiosError) => {
        globalErrorMessage.value = {
          i18Key: 'my-projects.errors.load-project-details-error',
          code: getAxiosResponseErrorCode(err),
          component: 'useReceivedOffers.ts',
        };
        return null;
      });
      isLoading.value = false;
      return project as unknown as ProjectWithOffers['project'];
    }
  );

  const offersWithProjects2ProjectsWithOffers = (offersWithProjects: ProjectOfferSearchResponse[], projectsToMerge?:ExternalUserProject[]):ProjectWithOffers[] => {
    const projectMap = new Map<string, ProjectWithOffers>();

    offersWithProjects.forEach((offerWithProject) => {
      const { project, ...offer } = offerWithProject;
      const projectId = project?.id;
      if (!projectId) return;

      let projectWithOffers: ProjectWithOffers | undefined = projectMap.get(projectId);

      project.status ??= 'ACTIVE';
      if (!projectWithOffers) {
        projectWithOffers = {
          id: projectId,
          offers: [],
          latestOfferCreated: '',
          project
        };
        if (!projectWithOffers) return; // TS can be quite stupid...

        projectMap.set(projectId, projectWithOffers);
      }

      projectWithOffers.offers.push(offer);
    });

    const projectsWithOffers = [...projectMap.values()];

    const remainingProjectsToMerge = (projectsToMerge?.filter((projectToMerge) => !projectsWithOffers.some((project) => projectToMerge.project?.id === project.id)) ?? []).map(
      (projectToMerge) => ({
        project: projectToMerge.project as unknown as Project, // as we have like 1000 project types that almost all contain the same stuff  :-/
        offers: [],
        latestOfferCreated: '',
        id: projectToMerge.project.id
      } as ProjectWithOffers)
    );

    return [...projectsWithOffers, ...remainingProjectsToMerge].map((item) => {
      const offersCreated = item.offers ? item.offers.map((offer) => offer.created).sort() : [];
      const latestOfferCreated = offersCreated?.pop() ?? '';
      return {
        ...item,
        latestOfferCreated
      } as ProjectWithOffers;
    });
  };

  type ClearCacheProps = {
    invalidateOfferId?: string;
    invalidateProjectId?: string;
    invalidateAll: boolean;
  }

  const loadProjectsWithOffers = async (invalidations?:ClearCacheProps) => {
    isLoading.value = true;
    hasChanges.value = false;
    const sPWOId = selectedProjectWithOffers.value?.id;

    if (invalidations?.invalidateAll) {
      loadProject.clear();
      loadOffer.clear();
      await loadMyProjects();
    } else if (invalidations?.invalidateProjectId) {
      loadProject.delete(invalidations.invalidateProjectId);

      if (invalidations?.invalidateOfferId) {
        loadOffer.delete(invalidations.invalidateProjectId, invalidations.invalidateOfferId);
      }
    }

    const searchOffersOrNothing = async (key: keyof ProjectOfferSearchRequest, value?: string): Promise<ProjectOfferSearchResponse[]> => {
      if (!value) return [];
      const request = {
        [key]: value
      };
      return searchOffers(request).catch((err: AxiosError) => {
        globalErrorMessage.value = {
          i18Key: 'my-projects.errors.load-offers-error',
          code: getAxiosResponseErrorCode(err),
          component: 'useReceivedOffers.ts',
        };
        return [];
      });
    };

    const [offersWithProjectsReceived, offersWithProjectsMade] = await Promise.all([
      companyId.value
        ? searchOffersOrNothing('projectCreatorCompanyId', companyId.value)
        : searchOffersOrNothing('projectCreatorId', userId.value),
      searchOffersOrNothing('creatorId', userId.value)
    ]);

    receivedProjectsWithOffers.value = offersWithProjects2ProjectsWithOffers(offersWithProjectsReceived, projectsCreatedByMe.value);
    madeProjectsWithOffers.value = offersWithProjects2ProjectsWithOffers(offersWithProjectsMade);

    // important to trigger updates further down the road:
    selectedProjectWithOffers.value = receivedProjectsWithOffers.value.find((owp) => owp.id === sPWOId) ?? null;

    isLoading.value = false;
  };

  const fullCheckForChanges = async () => {
    const [offersWithProjectsReceived, offersWithProjectsMade] = await Promise.all([
      companyId.value
        ? searchOffers({ projectCreatorCompanyId: companyId.value, projectId: selectedProjectWithOffers.value?.id })
        : searchOffers({ projectCreatorId: userId.value, projectId: selectedProjectWithOffers.value?.id }),
      searchOffers({ creatorId: userId.value })
    ]);

    const receivedProjectsWithOffersNew = offersWithProjects2ProjectsWithOffers(offersWithProjectsReceived, projectsCreatedByMe.value);
    const madeProjectsWithOffersNew = offersWithProjects2ProjectsWithOffers(offersWithProjectsMade);

    if (!selectedProjectWithOffers.value) {
      const totalOfferCountCurrent = receivedProjectsWithOffers.value.reduce((acc, cur) => acc + cur.offers.length, 0)
        + madeProjectsWithOffers.value.reduce((acc, cur) => acc + cur.offers.length, 0);
      const totalOfferCountNew = receivedProjectsWithOffersNew.reduce((acc, cur) => acc + cur.offers.length, 0)
        + madeProjectsWithOffersNew.reduce((acc, cur) => acc + cur.offers.length, 0);

      if (totalOfferCountCurrent !== totalOfferCountNew) {
        hasChanges.value = true;
        return;
      }
    } else {
      let selectedProjectWithOffersNew = receivedProjectsWithOffersNew.find((projectWithOffers) => projectWithOffers.id === selectedProjectWithOffers.value?.project?.id);

      if (!selectedProjectWithOffersNew) {
        selectedProjectWithOffersNew = madeProjectsWithOffersNew.find((projectWithOffers) => projectWithOffers.id === selectedProjectWithOffers.value?.project?.id);
      }

      if (selectedProjectWithOffersNew && selectedProjectWithOffersNew.offers.length > selectedProjectWithOffers.value.offers.length) {
        hasChanges.value = true;
        return;
      }
    }

    const hasOfferStatusChanged = (offersWithProjects: ProjectOfferSearchResponse[], projectsWithOffers: ProjectWithOffers[]) => offersWithProjects.some((offerWithProject) => {
      const existingOffer = projectsWithOffers
        .find((projectWithOffers) => projectWithOffers.id === offerWithProject.project?.id)
        ?.offers.find((o) => o.id === offerWithProject.id);

      // TODO Special case built in so that the pop-up does not appear to the creator of an offer when they extend it for the first time. This special rule should be removed in ticket BC-955.
      let isItSpecialCase = false;
      if (existingOffer && offerWithProject.status === 'OPEN_CHANGED' && existingOffer.status === 'OPEN' && offerWithProject.creator.userId === userId.value) isItSpecialCase = true;

      return (existingOffer && offerWithProject.status !== existingOffer.status) && !isItSpecialCase;
    });

    hasChanges.value = hasOfferStatusChanged(offersWithProjectsReceived, receivedProjectsWithOffers.value)
      || hasOfferStatusChanged(offersWithProjectsMade, madeProjectsWithOffers.value);
  };

  const reloadOffer = async (projectId: string, offerId: string) => {
    loadOffer.delete(projectId, offerId);
    const offer = await loadOffer(projectId, offerId);
    if (!offer) return;

    let projectWithOffers = receivedProjectsWithOffers.value.find((projectWithOffers) => projectWithOffers.id === projectId);
    if (!projectWithOffers) {
      projectWithOffers = madeProjectsWithOffers.value.find((projectWithOffers) => projectWithOffers.id === projectId);
    }
    if (!projectWithOffers) return;
    const offerIndex = projectWithOffers.offers.findIndex((o) => o.id === offerId);
    if (offerIndex < 0) return;
    projectWithOffers.offers.splice(offerIndex, 1, offer);
  };

  const checkOfferForChanges = async (projectId: string, offerId: string) => {
    isLoading.value = true;
    const offer = await getProjectOffer(projectId, offerId);
    let existingOffer = receivedProjectsWithOffers.value
      .find((projectWithOffers) => projectWithOffers.id === projectId)
      ?.offers.find((o) => o.id === offerId);
    if (!existingOffer) {
      existingOffer = madeProjectsWithOffers.value.find((projectWithOffers) => projectWithOffers.id === projectId)?.offers.find((o) => o.id === offerId);
    }

    if (!existingOffer) return;

    if (offer.status !== existingOffer.status) {
      await reloadOffer(projectId, offerId);
    }
    isLoading.value = false;
  };

  const fullReloadProjectsWithOffers = async () => {
    logDebug('reloadProjectsWithOffers');
    await loadProjectsWithOffers({ invalidateAll: true });
  };

  const offers = computed(() => selectedProjectWithOffers.value?.offers ?? []);
  const madeOffers = computed(() => []);

  const markOfferAsRead = (offerId: string) => {
    [
      ...receivedProjectsWithOffers.value,
      ...madeProjectsWithOffers.value
    ].forEach((projectsWithOffers) => {
      const offerIndex = projectsWithOffers.offers.findIndex((offer) => offer.id === offerId);
      if (offerIndex < 0) return;
      const offer = projectsWithOffers.offers[offerIndex];
      projectsWithOffers.offers.splice(
        offerIndex, 1, { ...offer, read: new Date().toISOString() }
      );
    });
  };

  watchEffect(() => {
    const projectId = route?.params?.projectId;
    if (!projectId) {
      selectedProjectWithOffers.value = null;
      return;
    }

    selectedProjectWithOffers.value = receivedProjectsWithOffers.value.find((project) => project?.id === projectId) ?? null;
  });

  const reloadAfterProjectChange = ref(true);

  return {
    loadProjectsWithOffers,
    fullReloadProjectsWithOffers,
    fullCheckForChanges,
    checkOfferForChanges,
    reloadOffer,
    hasChanges,
    selectedProjectWithOffers,
    offers,
    madeOffers,
    isStatusLayerOpen,
    isNarrow: options.isNarrow,
    // service pullthrough:
    isCustomer,
    isFreelancer,
    isLoading,
    madeProjectsWithOffers,
    loadOffer,
    loadProject,
    markOfferAsRead,
    statusLayerProject,
    projectIdsToKeepInProjectFilter,
    receivedProjectsWithOffers,
    loadMyProjects,
    myProjects,
    visibleReceivedProjectsWithOffers,
    reloadAfterProjectChange
  };
});

export { useMyProjectsProvider };

export function useMyProjects() {
  const store = useOffersInjected();
  if (store == null) throw new Error('Please call `useReceivedOffersProvider` on the appropriate parent component');
  return store;
}
